// Read-only replay of a recorded game on the game canvas: the shared
// transport bar underneath, the board drawn by the same renderer the live
// game uses (minus the gear). Stepping forward animates the move; seeking
// lands on the position at once.
import { ReplayControls, type ReplayControlsState } from '@bn-games/ui-shell';
import { t } from '../i18n/I18n.js';
import { themeToRenderPalette } from '../render/palette.js';
import { RafLoop } from '../scenes/RafLoop.js';
import { SceneAnimation } from '../scenes/SceneAnimation.js';
import { renderBoardFrame } from '../scenes/SceneRender.js';
import type { SettingsManager } from '../settings/SettingsManager.js';
import { THEMES } from '../settings/themes.js';
import { ReplayController } from './ReplayController.js';
import { buildReplayFrame, replayRotation } from './replayFrame.js';
import type { GameRecord } from './types.js';
import { ReplayPanel } from '../ui/ReplayPanel.js';

export type ReplayReturnTarget = 'history' | 'menu';

export interface ReplaySceneDeps {
  readonly canvas: HTMLCanvasElement;
  readonly ctx: CanvasRenderingContext2D;
  readonly settings: SettingsManager;
  readonly onExit: (target: ReplayReturnTarget) => void;
}

/** Pause between auto-played moves at 1× speed. */
const AUTO_PLAY_INTERVAL_MS = 900;
const MIN_AUTO_PLAY_INTERVAL_MS = 120;

export class ReplayScene {
  private readonly loop = new RafLoop(() => this.render());
  private readonly animation = new SceneAnimation();
  private controller: ReplayController | null = null;
  private controls: ReplayControls | null = null;
  private panel: ReplayPanel | null = null;
  private returnTo: ReplayReturnTarget = 'menu';
  private playing = false;
  private speed = 1;
  private timer: ReturnType<typeof setTimeout> | null = null;

  private readonly onKeyDown = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') this.exit();
    else if (e.key === 'ArrowRight') this.next();
    else if (e.key === 'ArrowLeft') this.seek(this.step() - 1);
    else return;
    e.preventDefault();
  };

  constructor(private readonly deps: ReplaySceneDeps) {}

  /** False when the record does not replay; nothing is mounted then. */
  open(record: GameRecord, returnTo: ReplayReturnTarget): boolean {
    this.close();
    const controller = ReplayController.open(record);
    if (controller === null) return false;
    this.controller = controller;
    this.returnTo = returnTo;
    controller.setListener(() => {
      this.controls?.update(this.controlsState());
      this.panel?.sync();
    });
    this.controls = new ReplayControls({
      onFirst: () => this.seek(0),
      onPrev: () => this.seek(this.step() - 1),
      onPlayPause: () => this.togglePlay(),
      onNext: () => this.next(),
      onLast: () => this.seek(controller.getTotalSteps()),
      onSpeed: (speed) => this.setSpeed(speed),
      onBack: () => this.exit(),
    }, this.controlsState());
    this.controls.mount();
    this.panel = new ReplayPanel(this.deps.canvas, controller, (plies) => this.seek(plies));
    window.addEventListener('keydown', this.onKeyDown);
    this.loop.start();
    return true;
  }

  close(): void {
    this.stopTimer();
    this.loop.stop();
    window.removeEventListener('keydown', this.onKeyDown);
    this.controls?.unmount();
    this.controls = null;
    this.panel?.dispose();
    this.panel = null;
    this.controller = null;
    this.playing = false;
    this.speed = 1;
    this.animation.clear();
  }

  exit(): void {
    if (this.controller === null) return;
    const target = this.returnTo;
    this.close();
    this.deps.onExit(target);
  }

  /** Jump to the position after `target` moves, without animating. */
  seek(target: number): void {
    this.setPlaying(false);
    this.animation.clear();
    this.controller?.seek(target);
  }

  private step(): number {
    return this.controller?.getStepIndex() ?? 0;
  }

  private next(): void {
    this.setPlaying(false);
    this.advance();
  }

  private advance(): boolean {
    const played = this.controller?.stepForward() ?? null;
    if (played === null) return false;
    this.animation.start({ move: played.move, before: played.before, by: 'remote' }, performance.now());
    return true;
  }

  private togglePlay(): void {
    if (this.playing) this.setPlaying(false);
    else if (this.controller?.isAtEnd() === false) {
      this.setPlaying(true);
      this.autoStep();
    }
  }

  private autoStep(): void {
    if (!this.playing) return;
    if (!this.advance() || this.controller?.isAtEnd() !== false) {
      this.setPlaying(false);
      return;
    }
    const delay = Math.max(MIN_AUTO_PLAY_INTERVAL_MS, AUTO_PLAY_INTERVAL_MS / this.speed);
    this.timer = setTimeout(() => this.autoStep(), delay);
  }

  private setPlaying(playing: boolean): void {
    if (!playing) this.stopTimer();
    if (playing === this.playing) return;
    this.playing = playing;
    this.controls?.update(this.controlsState());
  }

  private setSpeed(speed: number): void {
    this.speed = speed;
    this.controls?.update(this.controlsState());
  }

  private stopTimer(): void {
    if (this.timer !== null) clearTimeout(this.timer);
    this.timer = null;
  }

  private controlsState(): ReplayControlsState {
    return {
      stepIndex: this.step(),
      totalSteps: this.controller?.getTotalSteps() ?? 0,
      speed: this.speed,
      isPlaying: this.playing,
      backLabel: t(this.returnTo === 'history' ? 'replay.controls.backToHistory' : 'replay.controls.backToMenu'),
    };
  }

  private render(): void {
    const controller = this.controller;
    if (controller === null) return;
    const rotation = replayRotation(controller.getRecord());
    const frame = buildReplayFrame(controller, rotation, this.animation.frame(performance.now(), rotation));
    renderBoardFrame(this.deps.ctx, frame, themeToRenderPalette(THEMES[this.deps.settings.get().theme]));
    this.panel?.layout();
  }
}
